import React, { useState, useEffect } from "react";
import Sidebar from "../../../shared/components/Sidebar";
import BackButton from "../../../shared/components/Backbtn";
import {
  Box, Typography, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, CircularProgress,
  InputAdornment, Snackbar, Alert, Tooltip
} from "@mui/material";
import { Search, Block, CheckCircle } from "@mui/icons-material";
import { db } from "../../../context/Firebase";
import { collection, getDocs, doc, updateDoc, orderBy, query } from "firebase/firestore";

const ManageHashtags = () => {
  const [hashtags, setHashtags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    fetchHashtags();
  }, []);

  const fetchHashtags = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "hashtags"), orderBy("count", "desc"));
      const snap = await getDocs(q);
      setHashtags(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Error fetching hashtags:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleBlock = async (tag) => {
    const newBlocked = !tag.isBlocked;
    try {
      await updateDoc(doc(db, "hashtags", tag.id), {
        isBlocked: newBlocked,
        isReported: newBlocked ? tag.isReported : false,
      });
      setHashtags(prev =>
        prev.map(h => h.id === tag.id ? { ...h, isBlocked: newBlocked, isReported: newBlocked ? h.isReported : false } : h)
      );
      setSnackbar({
        open: true,
        message: `#${tag.id} has been ${newBlocked ? "blocked" : "unblocked"}.`,
        severity: newBlocked ? "warning" : "success",
      });
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to update hashtag.", severity: "error" });
    }
  };

  const toggleReport = async (tag) => {
    const newReported = !tag.isReported;
    try {
      await updateDoc(doc(db, "hashtags", tag.id), { isReported: newReported });
      setHashtags(prev =>
        prev.map(h => h.id === tag.id ? { ...h, isReported: newReported } : h)
      );
      setSnackbar({
        open: true,
        message: `#${tag.id} has been ${newReported ? "reported" : "unreported"}.`,
        severity: newReported ? "warning" : "success",
      });
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to update hashtag.", severity: "error" });
    }
  };

  const filtered = hashtags.filter(h =>
    h.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="dashboard">
      <Sidebar />
      <div className="dashboard--content">
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <BackButton tooltip="Back" />
        </Box>

        <Typography variant="h5" fontWeight={700} mb={3}>Manage Hashtags</Typography>

        <TextField
          placeholder="Search hashtags..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          size="small"
          sx={{ mb: 3, width: 320, bgcolor: "var(--input-bg)", borderRadius: 2 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search /></InputAdornment>
          }}
        />

        {loading ? (
          <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                <TableRow>
                  <TableCell><strong>Hashtag</strong></TableCell>
                  <TableCell align="center"><strong>Posts</strong></TableCell>
                  <TableCell align="center"><strong>Status</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4, color: "#999" }}>
                      No hashtags found
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map(tag => (
                    <TableRow key={tag.id} sx={{ "&:hover": { bgcolor: "var(--hover-bg)" } }}>
                      <TableCell>
                        <Typography 
                          fontWeight={600} 
                          className="hashtag-text"
                        >
                          #{tag.id}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{tag.count || 0}</TableCell>
                      <TableCell align="center">
                        {tag.isBlocked ? (
                          <Chip label="Blocked" color="error" size="small" icon={<Block />} />
                        ) : tag.isReported ? (
                          <Chip label="Reported" color="warning" size="small" />
                        ) : (
                          <Chip label="Active" color="success" size="small" icon={<CheckCircle />} />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" gap={1} justifyContent="center">
                          <Tooltip title={tag.isBlocked ? "Unblock" : "Block"}>
                            <Button
                              size="small"
                              variant={tag.isBlocked ? "outlined" : "contained"}
                              color={tag.isBlocked ? "success" : "error"}
                              onClick={() => toggleBlock(tag)}
                              startIcon={tag.isBlocked ? <CheckCircle /> : <Block />}
                            >
                              {tag.isBlocked ? "Unblock" : "Block"}
                            </Button>
                          </Tooltip>
                          <Tooltip title={tag.isReported ? "Clear Report" : "Mark Reported"}>
                            <Button
                              size="small"
                              variant="outlined"
                              color={tag.isReported ? "success" : "warning"}
                              onClick={() => toggleReport(tag)}
                              disabled={tag.isBlocked}
                            >
                              {tag.isReported ? "Clear" : "Report"}
                            </Button>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
};

export default ManageHashtags;
